import { BaseProvider } from "./base.provider";

export interface PaginatedCards {
  data: ProfileCard[];
  page: number;
  page_size: number;
  has_next: boolean;
}

export interface ProfileCard {
  profile_card_id: string;
  name: string;
  img_url: string | null;
  description: string | null;
  is_active: boolean;
  created_at: string;
  primary_color: string;
  secondary_color: string;
  font: string | null;
  embed_content: boolean;
}

export interface UploadUrlResponse {
  upload_url: string;
  form_fields: Record<string, string>;
}

export interface CreateCardResult extends UploadUrlResponse {
  card_id: string;
}

export interface CreateCardPayload {
  name: string;
  description?: string;
  primary_color: string;
  secondary_color: string;
  font: string;
}

export interface UpdateCardPayload {
  name: string;
  description: string;
  is_active: boolean;
  primary_color: string;
  secondary_color: string;
  font: string;
  embed_content: boolean;
}

class CardsProvider extends BaseProvider {
  listAll(): Promise<ProfileCard[]> {
    return this.request<ProfileCard[]>("/v1/admin/cards");
  }

  create(payload: CreateCardPayload): Promise<CreateCardResult> {
    return this.request<CreateCardResult>("/v1/admin/cards", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  getImageUploadUrl(cardId: string): Promise<UploadUrlResponse> {
    return this.request<UploadUrlResponse>(
      `/v1/admin/cards/${encodeURIComponent(cardId)}/image`,
      { method: "POST" }
    );
  }

  async uploadImageToStorage(
    { upload_url, form_fields }: UploadUrlResponse,
    file: File
  ): Promise<void> {
    const form = new FormData();
    for (const [key, value] of Object.entries(form_fields)) {
      form.append(key, value);
    }
    form.append("Content-Type", file.type);
    form.append("file", file);
    const res = await fetch(upload_url, { method: "POST", body: form });
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(text || `Storage upload failed: HTTP ${res.status}`);
    }
  }

  update(cardId: string, payload: UpdateCardPayload): Promise<void> {
    return this.request<void>(`/v1/admin/cards/${encodeURIComponent(cardId)}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  delete(cardId: string): Promise<void> {
    return this.request<void>(`/v1/admin/cards/${encodeURIComponent(cardId)}`, {
      method: "DELETE",
    });
  }

  async getCardsForUser(userId: string): Promise<ProfileCard[]> {
    const res = await this.request<PaginatedCards>(`/v1/admin/cards/user/${encodeURIComponent(userId)}`);
    return res.data;
  }

  grantToUser(cardId: string, userId: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/cards/${encodeURIComponent(cardId)}/grant/${encodeURIComponent(userId)}`,
      { method: "POST" }
    );
  }

  revokeFromUser(cardId: string, userId: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/cards/${encodeURIComponent(cardId)}/revoke/${encodeURIComponent(userId)}`,
      { method: "DELETE" }
    );
  }

  setActiveCard(userId: string, cardId: string): Promise<void> {
    return this.request<void>(
      `/v1/admin/users/${encodeURIComponent(userId)}/active-card/${encodeURIComponent(cardId)}`,
      { method: "PUT" }
    );
  }

  listOwned(page = 1): Promise<PaginatedCards> {
    return this.request<PaginatedCards>(`/v1/users/cards/owned?page=${page}`);
  }

  clearActiveCard(userId: string): Promise<void> {
    return this.request<void>("/v1/users/cards/active", { method: "DELETE" });
  }
}

export const cardsProvider = new CardsProvider();
